import React, { useEffect, useState } from 'react';
import { useTranslation, Locale } from '@rbx/intl';
import creatorHistoryClient from '@modules/clients/creatorHistoryClient';
import {
  trackSearchRecentlyVisitedClicked,
  trackSearchRecentlyVisitedDeleted,
  trackSearchRecentlyVisitedImpression,
  TResultClickedInteraction,
} from '@modules/search/searchEvents';
import { ESearchInteraction } from '@modules/eventStream/enum/DocsSiteSearch';
import { makeStyles } from '@rbx/ui';
import { SearchList } from './SearchList';
import { TSearchListItem } from './types/SearchListItem';
import { SearchListItemLinkProps } from './SearchListItem';

type SearchListNullStateProps = {
  impressionRef: React.MutableRefObject<string | null>;
  locale: Locale;
  onClickItem: SearchListItemLinkProps['onClickItem'];
  searchSessionId: string;
};

const useNullStateStyles = makeStyles()(() => ({
  emptyDiv: {
    marginTop: '-8px',
    height: '0px',
  },
}));

const SearchListNullState: React.FC<SearchListNullStateProps> = ({
  impressionRef,
  locale,
  onClickItem,
  searchSessionId,
}) => {
  const { translate } = useTranslation();
  const [recentlyVisited, setRecentlyVisited] = useState<TSearchListItem[]>([]);
  const { classes } = useNullStateStyles();
  useEffect(() => {
    const getRecentlyVisited = async () => {
      const items = await creatorHistoryClient.getRecentlyVisited();
      setRecentlyVisited(items);
    };
    getRecentlyVisited();
  }, []);

  const onRemoveRecentlyVisitedItem = async (id: string) => {
    const updatedItems = await creatorHistoryClient.removeFromRecentlyVisited(id);
    setRecentlyVisited(updatedItems);
  };

  useEffect(() => {
    if (
      !searchSessionId ||
      recentlyVisited.length === 0 ||
      impressionRef.current === searchSessionId
    ) {
      return;
    }
    // eslint-disable-next-line no-param-reassign
    impressionRef.current = searchSessionId;
    trackSearchRecentlyVisitedImpression({
      locale,
      recentlyVisited,
      searchSessionId,
    });
  }, [searchSessionId, recentlyVisited, impressionRef, locale]);

  const onRemoveItem = (id: string) => {
    const itemFromId = recentlyVisited.find((item) => item.id === id);
    if (itemFromId) {
      trackSearchRecentlyVisitedDeleted({
        interaction: ESearchInteraction.Click,
        item: itemFromId,
        locale,
        recentlyVisited,
        searchSessionId,
      });
    }
    onRemoveRecentlyVisitedItem(id);
  };

  const onClickRecentlyVisitedItem = (
    item: TSearchListItem,
    interaction: TResultClickedInteraction,
  ) => {
    trackSearchRecentlyVisitedClicked({
      interaction,
      item,
      locale,
      recentlyVisited,
      searchSessionId,
    });
    onClickItem(item, interaction);
  };

  return recentlyVisited.length > 0 ? (
    <SearchList
      title={translate('Label.RecentlyVisited') || 'Recently Visited'}
      isTitleClickable={false}
      items={recentlyVisited}
      onRemove={onRemoveItem}
      onClickItem={onClickRecentlyVisitedItem}
    />
  ) : (
    // NOTE (@tchu, 2025-08-22):
    // This is to remove the excess space in the search dialog when there are no recently visited items
    <div className={classes.emptyDiv} />
  );
};

export default React.memo(SearchListNullState);
